<template>
  <div class="run-records" v-loading="loading">
    <section class="summary-grid">
      <el-card v-for="card in summaryCards" :key="card.key" shadow="never" class="summary-card">
        <div class="summary-icon" :class="card.tone"><i :class="card.icon"></i></div>
        <div>
          <span>{{ card.label }}</span>
          <strong>{{ card.value }}</strong>
          <p>{{ card.desc }}</p>
        </div>
      </el-card>
    </section>

    <el-tabs v-model="activeSection" type="border-card" class="records-tabs">
      <el-tab-pane label="任务运行" name="runs">
        <div class="filter-row">
          <el-select v-model="filters.group" placeholder="全部任务" clearable size="small">
            <el-option label="市场事件监控" value="market_event_watch" />
            <el-option label="个股事件监控" value="stock_event_watch" />
            <el-option label="持仓上下文监控" value="position_context_watch" />
            <el-option label="系统健康检查" value="system_health_check" />
          </el-select>
          <el-select v-model="filters.status" placeholder="全部状态" clearable size="small">
            <el-option label="成功" value="success" />
            <el-option label="部分成功" value="partial_success" />
            <el-option label="失败" value="failed" />
            <el-option label="执行中" value="running" />
          </el-select>
          <el-input v-model.trim="filters.keyword" size="small" clearable placeholder="按失败说明或诊断编号搜索" />
          <el-button size="small" @click="clearFilters">重置</el-button>
          <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="refreshData">刷新</el-button>
        </div>

        <el-alert
          v-if="parentRunsLoadFailed"
          title="任务运行记录暂时不可用，请稍后刷新。"
          type="warning"
          :closable="false"
          show-icon
          class="inline-alert"
        />

        <el-table :data="filteredParentRuns" size="small" stripe empty-text="暂无任务运行记录">
          <el-table-column label="运行时间" width="175">
            <template slot-scope="scope">{{ formatBeijing(parentRunTime(scope.row)) }}</template>
          </el-table-column>
          <el-table-column label="任务" min-width="170">
            <template slot-scope="scope">
              <strong>{{ groupLabel(scope.row.group_id) }}</strong>
              <div class="sub-text">{{ triggerText(scope.row.trigger_source) }}</div>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="105">
            <template slot-scope="scope">
              <el-tag size="mini" :type="statusType(scope.row.status)">{{ statusText(scope.row.status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="结果" min-width="360">
            <template slot-scope="scope">{{ resultText(scope.row) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="100" fixed="right">
            <template slot-scope="scope">
              <el-button size="mini" type="primary" plain @click="showDetail(scope.row)">查看详情</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane label="通知发送" name="notifications">
        <div class="section-head">
          <div>
            <strong>通知发送记录</strong>
            <p>时间统一显示为北京时间；诊断编号保留为复制入口。</p>
          </div>
          <el-button size="small" icon="el-icon-refresh" :loading="notificationLoading" @click="loadNotifications">刷新</el-button>
        </div>

        <el-alert
          v-if="notificationLoadFailed"
          title="通知发送记录暂时不可用，任务运行记录不受影响。"
          type="warning"
          :closable="false"
          show-icon
          class="inline-alert"
        />

        <el-table :data="sortedNotifications" size="small" stripe empty-text="暂无通知发送记录">
          <el-table-column label="发送时间（北京时间）" width="195">
            <template slot-scope="scope">{{ formatBeijing(notificationTime(scope.row), true) }}</template>
          </el-table-column>
          <el-table-column label="渠道" width="80">
            <template slot-scope="scope"><el-tag size="mini" effect="plain">{{ channelText(scope.row.channel) }}</el-tag></template>
          </el-table-column>
          <el-table-column label="类型 / 对象" min-width="150">
            <template slot-scope="scope">{{ notificationTarget(scope.row) }}</template>
          </el-table-column>
          <el-table-column label="内容摘要" min-width="360" show-overflow-tooltip>
            <template slot-scope="scope">{{ notificationContent(scope.row) }}</template>
          </el-table-column>
          <el-table-column label="状态" width="100">
            <template slot-scope="scope">
              <el-tag size="mini" :type="notifyStatusType(scope.row.status)">{{ notifyStatusText(scope.row.status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="诊断编号" width="100" fixed="right">
            <template slot-scope="scope">
              <el-button
                v-if="notificationIdentifier(scope.row)"
                type="text"
                size="mini"
                @click="copyText(notificationIdentifier(scope.row))"
              >复制编号</el-button>
              <span v-else class="sub-text">暂无</span>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
    </el-tabs>

    <el-dialog title="任务运行详情" :visible.sync="detailVisible" width="880px" :close-on-click-modal="false">
      <div v-loading="detailLoading">
        <section v-if="detailParent" class="diagnosis-panel" :class="diagnosisTone(detailParent)">
          <div class="diagnosis-head">
            <div>
              <span>本次运行说明</span>
              <strong>{{ eventSummary(detailParent) }}</strong>
            </div>
            <el-button
              size="small"
              type="primary"
              plain
              icon="el-icon-document-copy"
              @click="copyDiagnosticInfo(detailParent)"
            >复制诊断信息</el-button>
          </div>
          <div class="diagnosis-grid">
            <div>
              <span>影响</span>
              <strong>{{ impactText(detailParent) }}</strong>
            </div>
            <div>
              <span>处理状态</span>
              <strong>{{ handlingStatusText(detailParent) }}</strong>
            </div>
            <div class="diagnosis-id">
              <span>诊断编号</span>
              <code>{{ diagnosticIdentifier(detailParent) || '暂无' }}</code>
            </div>
          </div>
        </section>

        <el-descriptions v-if="detailParent" :column="2" border size="small">
          <el-descriptions-item label="任务">{{ groupLabel(detailParent.group_id) }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag size="mini" :type="statusType(detailParent.status)">{{ statusText(detailParent.status) }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="运行时间" :span="2">
            {{ runPeriodText(detailParent) }}
          </el-descriptions-item>
          <el-descriptions-item label="运行范围" :span="2">{{ scopeText(detailParent) }}</el-descriptions-item>
          <el-descriptions-item label="结果摘要" :span="2">{{ resultText(detailParent) }}</el-descriptions-item>
          <el-descriptions-item v-if="isAbnormal(detailParent)" label="失败说明" :span="2">
            <span class="danger-text">{{ failureCause(detailParent) }}</span>
          </el-descriptions-item>
        </el-descriptions>

        <div v-if="detailChildren.length" class="children-section">
          <div class="section-head compact">
            <div><strong>内部模块</strong><p>只展示模块状态和结果；技术编号由管理员在后台查询。</p></div>
          </div>
          <el-table :data="detailChildren" size="small" border stripe>
            <el-table-column label="模块" min-width="145">
              <template slot-scope="scope">{{ moduleLabel(scope.row.task_type, scope.row.module_name) }}</template>
            </el-table-column>
            <el-table-column label="状态" width="90">
              <template slot-scope="scope">
                <el-tag size="mini" :type="childStatusType(scope.row.child_status)">{{ childStatusText(scope.row.child_status) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="新增 / 去重" width="105">
              <template slot-scope="scope">{{ countText(scope.row.new_items_count) }} / {{ countText(scope.row.deduped_count) }}</template>
            </el-table-column>
            <el-table-column label="结果" min-width="300">
              <template slot-scope="scope">{{ childResultText(scope.row) }}</template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <div slot="footer"><el-button @click="detailVisible = false">关闭</el-button></div>
    </el-dialog>
  </div>
</template>

<script>
import { notificationApi } from '@/api'
import notificationTaskGroupApi from '@/api/notificationTaskGroup'

export default {
  name: 'RunRecordsLightweight',
  data() {
    return {
      loading: false,
      parentRuns: [],
      parentRunsLoadFailed: false,
      notificationHistory: [],
      notificationLoading: false,
      notificationLoadFailed: false,
      activeSection: this.$route.query.section === 'notifications' ? 'notifications' : 'runs',
      filters: {
        group: this.$route.query.group || '',
        status: '',
        keyword: ''
      },
      detailVisible: false,
      detailLoading: false,
      detailParent: null,
      detailChildren: []
    }
  },
  computed: {
    filteredParentRuns() {
      const keyword = String(this.filters.keyword || '').trim().toLowerCase()
      return this.parentRuns.filter(run => {
        if (this.filters.group && run.group_id !== this.filters.group) return false
        if (this.filters.status && String(run.status || '').toLowerCase() !== this.filters.status) return false
        if (!keyword) return true
        return [
          this.diagnosticIdentifier(run),
          run.error_code,
          this.failureCause(run),
          this.groupLabel(run.group_id),
          this.resultText(run)
        ].join(' ').toLowerCase().includes(keyword)
      })
    },
    sortedNotifications() {
      return this.notificationHistory.slice().sort((a, b) => this.timeValue(this.notificationTime(b), true) - this.timeValue(this.notificationTime(a), true))
    },
    summaryCards() {
      const success = this.parentRuns.filter(run => String(run.status || '').toLowerCase() === 'success').length
      const failed = this.parentRuns.filter(run => String(run.status || '').toLowerCase() === 'failed').length
      const running = this.parentRuns.filter(run => ['running', 'pending'].includes(String(run.status || '').toLowerCase())).length
      const notificationFailed24h = this.notificationHistory.filter(item => (
        String(item.status || '').toLowerCase() === 'failed' && this.isWithinHours(this.notificationTime(item), 24, true)
      )).length
      return [
        { key: 'all', label: '最近任务运行', value: String(this.parentRuns.length), desc: '当前读取窗口', icon: 'el-icon-finished', tone: 'blue' },
        { key: 'success', label: '最近成功', value: String(success), desc: '完整结束的父任务', icon: 'el-icon-circle-check', tone: 'green' },
        { key: 'failed', label: '最近失败', value: String(failed), desc: running ? `另有 ${running} 条未结束` : '当前无运行中任务', icon: 'el-icon-warning-outline', tone: failed ? 'red' : 'gray' },
        { key: 'notify', label: '近24小时通知失败', value: String(notificationFailed24h), desc: notificationFailed24h ? '请查看通知发送' : '近期发送正常', icon: 'el-icon-message', tone: notificationFailed24h ? 'red' : 'green' }
      ]
    }
  },
  watch: {
    '$route.query.section'(value) {
      this.activeSection = value === 'notifications' ? 'notifications' : 'runs'
    },
    '$route.query.group'(value) {
      this.filters.group = value || ''
    }
  },
  mounted() {
    this.refreshData()
  },
  methods: {
    async refreshData() {
      this.loading = true
      try {
        await Promise.all([this.loadParentRuns(), this.loadNotifications()])
      } finally {
        this.loading = false
      }
    },
    async loadParentRuns() {
      try {
        const payload = this.unwrapResponse(await notificationTaskGroupApi.getParentRuns({ offset: 0, limit: 100 }))
        this.parentRuns = Array.isArray(payload.runs) ? payload.runs : (Array.isArray(payload.items) ? payload.items : [])
        this.parentRunsLoadFailed = false
      } catch (error) {
        this.parentRuns = []
        this.parentRunsLoadFailed = true
      }
    },
    async loadNotifications() {
      this.notificationLoading = true
      try {
        const payload = this.unwrapResponse(await notificationApi.getHistory({ limit: 50 }))
        if (Array.isArray(payload)) this.notificationHistory = payload
        else if (Array.isArray(payload.notifications)) this.notificationHistory = payload.notifications
        else if (Array.isArray(payload.logs)) this.notificationHistory = payload.logs
        else if (Array.isArray(payload.items)) this.notificationHistory = payload.items
        else this.notificationHistory = []
        this.notificationLoadFailed = false
      } catch (error) {
        this.notificationHistory = []
        this.notificationLoadFailed = true
      } finally {
        this.notificationLoading = false
      }
    },
    unwrapResponse(response) {
      return (response && (response.machine_payload || response.data || response.payload)) || response || {}
    },
    clearFilters() {
      this.filters = { group: '', status: '', keyword: '' }
      if (this.$route.query.group) this.$router.replace({ path: '/scheduled-task-runs', query: this.activeSection === 'notifications' ? { section: 'notifications' } : {} })
    },
    async showDetail(run) {
      this.detailVisible = true
      this.detailLoading = true
      this.detailParent = run
      this.detailChildren = []
      try {
        const payload = this.unwrapResponse(await notificationTaskGroupApi.getParentRun(run.parent_run_id))
        if (payload.parent) this.detailParent = payload.parent
        if (Array.isArray(payload.children)) this.detailChildren = payload.children
      } catch (error) {
        this.$message.warning('详细模块记录暂时不可用，已保留总体运行结果。')
      } finally {
        this.detailLoading = false
      }
    },
    parentForNotification(row) {
      if (!row) return null
      const id = String(row.title || row.batch_log_id || row.task_run_id || '')
      return this.parentRuns.find(run => run.parent_run_id === id) || null
    },
    groupLabel(groupId) {
      return {
        market_event_watch: '市场事件监控',
        stock_event_watch: '个股事件监控',
        position_context_watch: '持仓上下文监控',
        system_health_check: '系统健康检查'
      }[groupId] || '未识别任务'
    },
    triggerText(value) {
      return { manual_parent: '手动运行', scheduled_parent: '计划运行', retry_parent: '失败重试' }[value] || '系统运行'
    },
    parentRunTime(run) {
      return run && (run.started_at || run.scheduled_for || run.run_time_bj || run.run_time || run.created_at || run.completed_at) || ''
    },
    runPeriodText(run) {
      const start = this.formatBeijing(this.parentRunTime(run))
      const end = this.formatBeijing(run && run.completed_at)
      return end === '-' ? start : `${start} 至 ${end}`
    },
    statusText(status) {
      return { pending: '等待执行', running: '执行中', success: '成功', partial_success: '部分成功', failed: '失败' }[String(status || '').toLowerCase()] || '未知'
    },
    statusType(status) {
      const value = String(status || '').toLowerCase()
      if (value === 'success') return 'success'
      if (value === 'partial_success' || value === 'running' || value === 'pending') return 'warning'
      if (value === 'failed') return 'danger'
      return 'info'
    },
    diagnosisTone(run) {
      const value = String(run && run.status || '').toLowerCase()
      if (value === 'success') return 'success'
      if (value === 'failed') return 'danger'
      if (value === 'partial_success' || value === 'running' || value === 'pending') return 'warning'
      return 'info'
    },
    isAbnormal(run) {
      return ['failed', 'partial_success'].includes(String(run && run.status || '').toLowerCase())
    },
    diagnosticIdentifier(run) {
      if (!run) return ''
      const candidates = [
        run.diagnostic_id,
        run.incident_id,
        run.parent_run_id,
        run.run_id,
        run.batch_log_id,
        run.log_id
      ].map(value => String(value || '').trim()).filter(Boolean)
      return candidates[0] || ''
    },
    eventSummary(run) {
      const status = String(run && run.status || '').toLowerCase()
      if (status === 'success') return '任务已正常完成'
      if (status === 'partial_success') return '部分模块未完成'
      if (status === 'failed') return this.failureCause(run)
      if (status === 'running') return '任务正在执行'
      if (status === 'pending') return '任务等待执行'
      return '运行状态待确认'
    },
    impactText(run) {
      const status = String(run && run.status || '').toLowerCase()
      if (status === 'success') return '无影响'
      if (status === 'partial_success') return '部分模块结果缺失'
      if (status === 'failed') return '本次任务未生成完整结果'
      if (status === 'running' || status === 'pending') return '尚未产生异常影响'
      return '影响待确认'
    },
    handlingStatusText(run) {
      const raw = String(run && (run.handling_status || run.resolution_status) || '').toLowerCase()
      const explicit = {
        resolved: '已处理', closed: '已处理', processing: '管理员处理中',
        investigating: '管理员排查中', pending: '等待管理员排查'
      }[raw]
      if (explicit) return explicit
      const status = String(run && run.status || '').toLowerCase()
      if (status === 'success') return '无需处理'
      if (status === 'failed' || status === 'partial_success') return '等待管理员排查'
      if (status === 'running') return '系统执行中'
      if (status === 'pending') return '等待系统执行'
      return '待确认'
    },
    looksTechnical(text) {
      const value = String(text || '').toLowerCase()
      const tokens = [
        'traceback', 'exception', 'valueerror', 'typeerror', 'request_id', 'log_id',
        'task_id', 'mcp_', '.py', '/opt/', 'app/services/', 'select ', 'insert '
      ]
      return tokens.some(token => value.includes(token))
    },
    failureCause(run) {
      if (!run) return '任务未正常完成，管理员将根据诊断编号排查。'
      const codeLabel = this.errorLabel(run.error_code)
      const messages = [run.user_message, run.error_summary, run.visible_stub, run.error_message]
      for (const message of messages) {
        const text = String(message || '').trim()
        if (!text) continue
        if (/超时/.test(text)) return '任务执行超时'
        if (/目录不存在|文件不存在|not found/i.test(text)) return '运行所需资源暂不可用'
        if (/日期格式|format/i.test(text)) return '任务参数格式不符合要求'
        if (/白名单|whitelist/i.test(text)) return '本次管理操作未获授权'
        if (/最多读取|超过限制|limit/i.test(text)) return '本次查询范围超过限制'
        if (/[一-龥]/.test(text) && !this.looksTechnical(text)) return text.slice(0, 160)
      }
      return codeLabel || '任务未正常完成，管理员将根据诊断编号排查。'
    },
    resultText(run) {
      if (!run) return '结果暂不可用'
      const status = String(run.status || '').toLowerCase()
      const planned = Number(run.planned_module_count || 0)
      const success = Number(run.success_count || 0)
      const failed = Number(run.failed_count || 0)
      const skipped = Number(run.skipped_count || 0)
      const notStarted = Number(run.not_started_count || 0)
      const added = Number(run.new_items_count)
      const deduped = Number(run.deduped_count)
      const counts = []
      if (Number.isFinite(added)) counts.push(`新增 ${added} 条`)
      if (Number.isFinite(deduped) && deduped > 0) counts.push(`重复过滤 ${deduped} 条`)
      let base = '结果暂不可用'
      if (status === 'success') base = `成功，${planned ? `${success || planned}/${planned} 个模块完成` : '运行完成'}`
      else if (status === 'partial_success') base = `部分成功，成功 ${success}，失败 ${failed}，跳过 ${skipped}`
      else if (status === 'failed') base = `失败，${failed || 1} 个模块失败${notStarted ? `，${notStarted} 个未启动` : ''}；${this.failureCause(run)}`
      else if (status === 'running') base = '正在执行'
      else if (status === 'pending') base = `等待执行，共 ${planned} 个模块`
      return counts.length ? `${base}；${counts.join('，')}` : base
    },
    scopeText(run) {
      const snapshot = run && run.config_snapshot
      if (!snapshot || typeof snapshot !== 'object') return '按任务配置运行'
      return {
        system_managed: '全市场重大事件', first_book_all: '第一本账全范围',
        formal_all: '第一本账正式区', custom: '自定义范围', global: '全系统'
      }[snapshot.scope_type] || '按任务配置运行'
    },
    moduleLabel(taskType, moduleName) {
      return moduleName || {
        announcement_watch: '公司公告', news_watch: '市场新闻', stock_news_watch: '个股新闻',
        irm_qa_watch: '董秘问答', system_health_check: '系统健康'
      }[taskType] || '内部模块'
    },
    childStatusText(status) {
      return { planned: '等待执行', running: '执行中', success: '成功', failed: '失败', skipped: '已跳过', not_started: '未启动' }[String(status || '').toLowerCase()] || '未知'
    },
    childStatusType(status) {
      const value = String(status || '').toLowerCase()
      if (value === 'success') return 'success'
      if (value === 'failed') return 'danger'
      if (value === 'running' || value === 'planned') return 'warning'
      return 'info'
    },
    childResultText(child) {
      if (!child) return '结果暂不可用'
      const status = String(child.child_status || '').toLowerCase()
      if (status === 'failed' || status === 'not_started') return this.failureCause(child)
      if (status === 'running') return '正在执行'
      if (status === 'planned') return '等待执行'
      if (status === 'skipped') return '已跳过'
      if (status === 'success') return child.task_type === 'system_health_check' ? '系统检查完成' : `新增 ${this.countText(child.new_items_count)}，重复过滤 ${this.countText(child.deduped_count)}`
      return '结果暂不可用'
    },
    errorLabel(code) {
      const value = String(code || '').toUpperCase()
      const map = {
        CHILD_EXECUTION_FAILED: '子模块执行失败',
        CHILD_HANDLER_EXECUTION_FAILED: '子模块执行失败',
        CHILD_HANDLER_FAILURE_PERSISTENCE: '子模块失败状态写入异常',
        CHILD_HANDLER_TERMINAL_CONTRACT: '子模块未正常结束',
        CHILD_TERMINAL_TIMEOUT: '子模块执行超时',
        CHILD_DISPATCH_EXCEPTION: '子模块启动异常',
        CHILD_PRIVATE_HANDLER_UNAVAILABLE: '子模块执行入口不可用',
        CHILD_RUN_IDENTITY_MISMATCH: '子模块运行记录不一致'
      }
      return map[value] || (code ? '运行异常，等待管理员排查' : '')
    },
    countText(value) {
      const number = Number(value)
      return Number.isFinite(number) ? String(number) : '0'
    },
    notificationTime(row) {
      return row && (row.sent_at || row.delivered_at || row.created_at || row.updated_at || row.time) || ''
    },
    channelText(channel) {
      return String(channel || '').toLowerCase() === 'email' ? '邮件' : '飞书'
    },
    notificationTarget(row) {
      const parent = this.parentForNotification(row)
      if (parent) return this.groupLabel(parent.group_id)
      if (row && row.stock_name && row.ts_code) return `${row.stock_name} · ${row.ts_code}`
      const title = String(row && row.title || '')
      if (title.startsWith('SYS-')) return '系统健康检查'
      if (title.startsWith('ANN-')) return '公司公告'
      if (title.startsWith('NEWS-')) return '新闻监控'
      if (title.startsWith('IRM-')) return '董秘问答'
      if (title.startsWith('FS-')) return '飞书测试'
      return row && (row.stock_name || row.ts_code) || '系统通知'
    },
    notificationContent(row) {
      const parent = this.parentForNotification(row)
      if (parent) return `${this.groupLabel(parent.group_id)}：${this.resultText(parent)}`
      const explicit = row && (row.content || row.message || row.visible_stub)
      if (explicit && !this.looksTechnical(explicit)) return explicit
      const title = String(row && row.title || '')
      if (title.startsWith('SYS-')) return '系统健康检查通知'
      if (title.startsWith('ANN-')) return '公司公告监控通知'
      if (title.startsWith('NEWS-')) return '新闻监控通知'
      if (title.startsWith('IRM-')) return '董秘问答监控通知'
      if (title.startsWith('FS-')) return '飞书通道测试通知'
      return title && !/^(PRUN|RUN)-/.test(title) ? title : '通知已发送，可通过诊断编号交由管理员反查'
    },
    notificationIdentifier(row) {
      if (!row) return ''
      const candidates = [row.diagnostic_id, row.batch_log_id, row.task_run_id, row.run_id, row.title, row.log_id]
        .map(value => String(value || '').trim())
        .filter(Boolean)
      return candidates.find(value => /^(PRUN|RUN|ANN|NEWS|IRM|SYS|FS)-/.test(value)) || candidates[0] || ''
    },
    notifyStatusText(status) {
      const value = String(status || '').toLowerCase()
      return { pending: '待发送', delivered: '已送达', sent: '已发送', success: '已发送', failed: '失败' }[value] || '未知'
    },
    notifyStatusType(status) {
      const value = String(status || '').toLowerCase()
      if (['delivered', 'sent', 'success'].includes(value)) return 'success'
      if (value === 'failed') return 'danger'
      if (value === 'pending') return 'warning'
      return 'info'
    },
    normalizeDate(value, assumeUtc) {
      if (!value) return null
      if (value instanceof Date) return value
      let text = String(value).trim().replace(' ', 'T')
      if (!/(Z|[+-]\d{2}:?\d{2})$/.test(text)) text += assumeUtc ? 'Z' : '+08:00'
      const date = new Date(text)
      return Number.isNaN(date.getTime()) ? null : date
    },
    timeValue(value, assumeUtc = false) {
      const date = this.normalizeDate(value, assumeUtc)
      return date ? date.getTime() : 0
    },
    formatBeijing(value, assumeUtc = false) {
      const date = this.normalizeDate(value, assumeUtc)
      if (!date) return '-'
      const parts = new Intl.DateTimeFormat('zh-CN', {
        timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
      }).formatToParts(date).reduce((acc, item) => { acc[item.type] = item.value; return acc }, {})
      return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`
    },
    isWithinHours(value, hours, assumeUtc = false) {
      const timestamp = this.timeValue(value, assumeUtc)
      return timestamp > 0 && Date.now() - timestamp <= hours * 3600000
    },
    copyDiagnosticInfo(run) {
      if (!run) return
      const failedModules = this.detailChildren
        .filter(child => ['failed', 'not_started'].includes(String(child.child_status || '').toLowerCase()))
        .map(child => `${this.moduleLabel(child.task_type, child.module_name)}：${this.childResultText(child)}`)
      const lines = [
        '小七运行诊断信息',
        `诊断编号：${this.diagnosticIdentifier(run) || '暂无'}`,
        `任务：${this.groupLabel(run.group_id)}`,
        `状态：${this.statusText(run.status)}`,
        `运行时间：${this.runPeriodText(run)}`,
        `发生了什么：${this.eventSummary(run)}`,
        `影响：${this.impactText(run)}`,
        `处理状态：${this.handlingStatusText(run)}`,
        `结果：${this.resultText(run)}`
      ]
      if (failedModules.length) lines.push(`异常模块：${failedModules.join('；')}`)
      this.copyToClipboard(lines.join('\n'), '诊断信息已复制')
    },
    copyText(text) {
      this.copyToClipboard(text, '诊断编号已复制')
    },
    copyToClipboard(text, successMessage) {
      if (!text) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
          .then(() => this.$message.success(successMessage || '已复制'))
          .catch(() => this.$message.info(text))
      } else {
        this.$message.info(text)
      }
    }
  }
}
</script>

<style scoped>
.run-records { max-width: 1460px; margin: 0 auto; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin-bottom: 16px; }
.summary-card { border-radius: 14px; }
.summary-card /deep/ .el-card__body { display: flex; align-items: center; gap: 14px; padding: 16px 18px; }
.summary-icon { width: 42px; height: 42px; border-radius: 13px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 20px; background: #94a3b8; }
.summary-icon.blue { background: #2563eb; } .summary-icon.green { background: #16a34a; } .summary-icon.red { background: #ef4444; } .summary-icon.gray { background: #94a3b8; }
.summary-card span { color: #64748b; font-size: 12px; } .summary-card strong { display: block; margin: 3px 0; color: #0f172a; font-size: 23px; } .summary-card p { margin: 0; color: #94a3b8; font-size: 12px; }
.records-tabs { border-radius: 14px; overflow: hidden; }
.filter-row { display: grid; grid-template-columns: 190px 150px minmax(260px, 1fr) 75px 80px; gap: 10px; margin-bottom: 14px; }
.section-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; margin-bottom: 14px; }
.section-head.compact { margin-top: 18px; }
.section-head strong { color: #0f172a; }
.section-head p { margin: 4px 0 0; color: #64748b; font-size: 12px; }
.inline-alert { margin-bottom: 14px; }
.sub-text { margin-top: 4px; color: #94a3b8; font-size: 12px; }
.children-section { margin-top: 18px; }
.diagnosis-panel { margin-bottom: 16px; padding: 16px; border: 1px solid #dbe7f5; border-radius: 14px; background: #f7faff; }
.diagnosis-panel.success { border-color: #ccebd7; background: #f3fbf6; }
.diagnosis-panel.warning { border-color: #f4dfae; background: #fffaf0; }
.diagnosis-panel.danger { border-color: #f3c8c8; background: #fff6f6; }
.diagnosis-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.diagnosis-head span, .diagnosis-grid span { display: block; color: #64748b; font-size: 12px; }
.diagnosis-head strong { display: block; margin-top: 5px; color: #0f172a; font-size: 17px; line-height: 1.5; }
.diagnosis-grid { display: grid; grid-template-columns: 1fr 1fr minmax(260px, 1.4fr); gap: 12px; margin-top: 14px; }
.diagnosis-grid > div { min-width: 0; padding: 12px; border-radius: 11px; background: rgba(255, 255, 255, 0.78); }
.diagnosis-grid strong { display: block; margin-top: 5px; color: #334155; font-size: 13px; line-height: 1.5; }
.diagnosis-id code { display: block; margin-top: 5px; color: #1d4ed8; font-family: Consolas, Monaco, monospace; font-size: 12px; word-break: break-all; }
.danger-text { color: #ef4444; }
@media (max-width: 1000px) { .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .filter-row { grid-template-columns: 1fr; } .diagnosis-grid { grid-template-columns: 1fr; } }
@media (max-width: 760px) { .section-head, .diagnosis-head { align-items: flex-start; flex-direction: column; } .summary-grid { grid-template-columns: 1fr; } }
</style>